import {
  Button,
  Card,
  Table,
  Badge,
  Popconfirm,
  Space,
  Spin,
  Row,
  Col,
  Tag,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { BreadCrumb } from "../../../components/main/LayoutComponents";
import { Locale, ApiManager } from "../../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../../utils/helpers/helpers";
import { merchantHasCommission } from "../../../utils/helpers/merchantHelpers";
import { getOne } from "../../../utils/http/dataProvider";
import {
  attachCoursesToAlbum,
  detachCoursesFromAlbum,
} from "../../../utils/http/albumDataProvider";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import CourseSelectionModal from "./CourseSelectionModal";

import "./AlbumDetail.scss";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();

const AlbumDetail = () => {
  const user = useSelector((state) => getUserInfo(state));
  const history = useHistory();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [courseModalVisible, setCourseModalVisible] = useState(false);

  const { data: album, error, isLoading } = useQuery(
    ["course-album", id, user.token],
    () => getOne("course/albums", { id, token: user.token }),
    {
      enabled: !!id,
    }
  );

  // Fetch agent levels for commission display
  const { data: agentLevelsData } = useQuery(
    ["agent-levels", user.token],
    async () => {
      if (!user.token) {
        return null;
      }
      const apiUrl = api.agent.list;
      const apiConfig = {
        token: user.token,
      };
      const res = await apiUrl.run(apiConfig);
      if (res.result) {
        return res.data;
      }
      return null;
    },
    {
      enabled: !!user.token && merchantHasCommission(user),
    }
  );

  const agentLevels = agentLevelsData || [];

  if (error) {
    showNotificationWithIcon(error.data?.message || error.message);
  }

  const attachCoursesMutation = useMutation(
    ({ albumId, courseIds }) => attachCoursesToAlbum(albumId, courseIds, user.token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["course-album", id]);
        queryClient.invalidateQueries("course-albums");
        setLoading(false);
        showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const detachCoursesMutation = useMutation(
    ({ albumId, courseIds }) => detachCoursesFromAlbum(albumId, courseIds, user.token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["course-album", id]);
        queryClient.invalidateQueries("course-albums");
        setLoading(false);
        showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const handleAddCourses = (courses) => {
    const courseIds = courses.map((c) => c.id);
    setLoading(true);
    attachCoursesMutation.mutate({ albumId: id, courseIds });
    setCourseModalVisible(false);
  };

  const handleRemoveCourse = (courseId) => {
    setLoading(true);
    detachCoursesMutation.mutate({ albumId: id, courseIds: [courseId] });
  };

  const calculateDiscount = () => {
    if (!album) return 0;
    const originalPrice = album.original_price || 0;
    const albumPrice = album.price || 0;
    if (originalPrice > 0) {
      return ((originalPrice - albumPrice) / originalPrice * 100).toFixed(0);
    }
    return 0;
  };

  const courseColumns = [
    {
      title: i18n.t("course.courseInfo"),
      render: (item) => {
        return (
          <div className="info">
            <div className="cover">
              <img
                src={i18n.getPropNameByLocale(item, "cover")}
                alt={i18n.getPropNameByLocale(item, "name")}
                width={60}
                height={60}
                style={{ objectFit: "cover" }}
              />
            </div>
            <div className="name">
              <span>{i18n.getPropNameByLocale(item, "name")}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: i18n.t("course.type"),
      render: (item) => {
        return <span>{item.type_label}</span>;
      },
      width: 120,
    },
    {
      title: i18n.t("course.price"),
      render: (item) => {
        return <span>¥{item.price?.toFixed(2) || "0.00"}</span>;
      },
      width: 100,
    },
    {
      title: i18n.t("users.actions"),
      width: 150,
      render: (item) => {
        return (
          <Popconfirm
            title={i18n.t("course.album.confirmRemoveCourse")}
            onConfirm={() => handleRemoveCourse(item.id)}
            okText={i18n.t("base.modal.okText")}
            cancelText={i18n.t("base.modal.cancelText")}
          >
            <Button danger type="link" size="small" icon={<DeleteOutlined />}>
              {i18n.t("course.album.removeCourse")}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const breadCrumbData = [
    {
      href: "/main/courses/albums",
      title: i18n.t("course.album.title"),
    },
    {
      title: album ? i18n.getPropNameByLocale(album, "name") : "",
    },
  ];

  if (isLoading) {
    return <Spin spinning={isLoading} />;
  }

  if (!album) {
    return <div>{i18n.t("course.album.noCourses")}</div>;
  }

  const albumNotForSale =
    album.not_for_sale === true ||
    album.not_for_sale === 1 ||
    album.not_for_sale === "1";
  const discount = calculateDiscount();
  const totalCoursesPrice = album.courses
    ? album.courses.reduce((sum, course) => sum + (course.price || 0), 0)
    : 0;

  // Get agent level name by ID
  const getAgentLevelName = (agentLevelId) => {
    const level = agentLevels.find((level) => level.id === agentLevelId);
    return level ? i18n.getPropNameByLocale(level, "name") : `Level ${agentLevelId}`;
  };

  // Process commission data from album
  const getCommissionData = () => {
    if (!album) return [];
    
    // Check both formats: data.commissions (array format) and data.agent_level_commissions (object format)
    if (album.commissions && Object.keys(album.commissions).length > 0) {
      return Object.keys(album.commissions).map((levelId) => ({
        levelId: album.commissions[levelId].agent_level_id || parseInt(levelId),
        commission: album.commissions[levelId].commission,
        parent_commission: album.commissions[levelId].parent_commission,
      }));
    } else if (album.agent_level_commissions && Object.keys(album.agent_level_commissions).length > 0) {
      return Object.keys(album.agent_level_commissions).map((levelId) => ({
        levelId: parseInt(levelId),
        commission: album.agent_level_commissions[levelId].commission,
        parent_commission: album.agent_level_commissions[levelId].parent_commission,
      }));
    }
    return [];
  };

  const commissionData = getCommissionData();

  return (
    <div className="album-detail-page">
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={loading || isLoading}>
        <Row gutter={16}>
          <Col span={24}>
            <Card
              title={i18n.t("course.basicInfo")}
              className="album-info-card"
              extra={
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    history.push({
                      pathname: "/main/courses/albums/edit",
                      state: { id: album.id },
                    });
                  }}
                >
                  {i18n.t("product.productItem.actionEdit")}
                </Button>
              }
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("course.album.name")}:</label>
                    <span>{i18n.getPropNameByLocale(album, "name")}</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("course.categoryName")}:</label>
                    <span>
                      {i18n.getPropNameByLocale(album.category, "name") || "-"}
                    </span>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("common.statusLabel")}:</label>
                    <Badge
                      status={album.status === 1 ? "success" : "default"}
                      text={album.status_label}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="info-item">
                    <label>{i18n.t("course.album.order")}:</label>
                    <span>{album.order || 0}</span>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="cover-images">
                    <div className="cover-item">
                      <label>{i18n.t("course.album.zhCover")}:</label>
                      <img
                        src={album.zh_cover || ""}
                        alt="Chinese Cover"
                        style={{ maxWidth: 200, maxHeight: 150 }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                    <div className="cover-item">
                      <label>{i18n.t("course.album.ugCover")}:</label>
                      <img
                        src={album.ug_cover || ""}
                        alt="Uyghur Cover"
                        style={{ maxWidth: 200, maxHeight: 150 }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col span={24}>
            <Card title={i18n.t("course.album.pricing")} className="pricing-card">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <div className="info-item">
                    <label>{i18n.t("course.saleTypeFilter")}:</label>
                    <Badge
                      status={albumNotForSale ? "warning" : "success"}
                      text={
                        albumNotForSale
                          ? i18n.t("course.notForSale")
                          : i18n.t("course.forSale")
                      }
                    />
                  </div>
                </Col>
                <Col span={8}>
                  <div className="info-item">
                    <label>{i18n.t("course.album.marketPrice")}:</label>
                    <span>
                      {albumNotForSale
                        ? "—"
                        : `¥${album.market_price?.toFixed(2) || "0.00"}`}
                    </span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="info-item">
                    <label>{i18n.t("course.album.originalPrice")}:</label>
                    <span>
                      {albumNotForSale
                        ? "—"
                        : `¥${album.original_price?.toFixed(2) || "0.00"}`}
                    </span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="info-item">
                    <label>{i18n.t("course.album.price")}:</label>
                    <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                      {albumNotForSale
                        ? "—"
                        : `¥${album.price?.toFixed(2) || "0.00"}`}
                    </span>
                  </div>
                </Col>
                {!albumNotForSale && discount > 0 && (
                  <Col span={8}>
                    <div className="info-item">
                      <label>{i18n.t("course.album.discount")}:</label>
                      <Tag color="red">{discount}%</Tag>
                    </div>
                  </Col>
                )}
              </Row>
            </Card>
          </Col>

          {merchantHasCommission(user) && commissionData.length > 0 && (
            <Col span={24}>
              <Card title={i18n.t("course.commissionInfo")} className="commission-card">
                <Row gutter={[16, 16]}>
                  {commissionData.map((comm) => (
                    <Col span={24} key={comm.levelId}>
                      <Card
                        size="small"
                        title={getAgentLevelName(comm.levelId)}
                        style={{ marginBottom: 8 }}
                      >
                        <Row gutter={[16, 8]}>
                          <Col span={12}>
                            <div className="info-item">
                              <label>{i18n.t("agentLevel.commission")}:</label>
                              <span style={{ fontWeight: "bold", color: "#1890ff" }}>
                                ¥{comm.commission?.toFixed(2) || "0.00"}
                              </span>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div className="info-item">
                              <label>{i18n.t("agentLevel.parentCommission")}:</label>
                              <span style={{ fontWeight: "bold", color: "#52c41a" }}>
                                ¥{comm.parent_commission?.toFixed(2) || "0.00"}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          )}

          <Col span={24}>
            <Card title={i18n.t("course.album.settings")} className="settings-card">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <div className="info-item">
                    <label>{i18n.t("course.album.sales")}:</label>
                    <span>{album.actual_sold || 0}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="info-item">
                    <label>{i18n.t("course.album.views")}:</label>
                    <span>{album.actual_view || 0}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="info-item">
                    <label>{i18n.t("course.validDate")}:</label>
                    <span>
                      {album.valid_forever === 1
                        ? i18n.t("course.album.validForever")
                        : `${album.valid_days || 0} ${i18n.t("common.dayUnit")}`}
                    </span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="info-item">
                    <label>{i18n.t("users.date")}:</label>
                    <span>{album.created_at}</span>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title={i18n.t("course.album.courseManagement")}
              className="courses-card"
              extra={
                <Button
                  type="primary"
                  onClick={() => setCourseModalVisible(true)}
                >
                  {i18n.t("course.album.addCourses")}
                </Button>
              }
            >
              {album.courses && album.courses.length > 0 ? (
                <>
                  <Table
                    className="list"
                    rowKey="id"
                    columns={courseColumns}
                    dataSource={album.courses}
                    pagination={false}
                  />
                  <div
                    style={{
                      marginTop: 16,
                      padding: "12px",
                      background: "#f5f5f5",
                      borderRadius: "4px",
                    }}
                  >
                    <Space>
                      <span>
                        {i18n.t("course.album.coursesCount")}: {album.courses.length}
                      </span>
                      <span style={{ marginLeft: 16 }}>
                        {i18n.t("course.album.totalCoursesPrice")}:{" "}
                        <span style={{ fontWeight: "bold" }}>
                          ¥{totalCoursesPrice.toFixed(2)}
                        </span>
                      </span>
                      <span style={{ marginLeft: 16 }}>
                        {i18n.t("course.album.albumPrice")}:{" "}
                        <span
                          style={{ fontWeight: "bold", color: "#ff4d4f" }}
                        >
                          {albumNotForSale
                            ? i18n.t("course.notForSale")
                            : `¥${album.price?.toFixed(2) || "0.00"}`}
                        </span>
                      </span>
                    </Space>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#999",
                  }}
                >
                  {i18n.t("course.album.noCourses")}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Spin>

      <CourseSelectionModal
        visible={courseModalVisible}
        onCancel={() => setCourseModalVisible(false)}
        selectedCourseIds={album.courses ? album.courses.map((c) => c.id) : []}
        excludeCourseIds={album.courses ? album.courses.map((c) => c.id) : []}
        onCoursesSelected={handleAddCourses}
      />
    </div>
  );
};

export default AlbumDetail;

